import { getBridge } from "../bridge";

// Thin orchestration over the bridge's media cache. Prefetch the whole
// schedule (playlist + screen default) so playback never blocks on the
// network, and evict media no longer in the schedule.

// Structural shape shared by PlaylistItem and ScreenDefaultItem — only what caching needs.
type Cacheable = { media_id: string; media_url: string; checksum?: string | null };

export async function prefetchPlaylist(...groups: Cacheable[][]): Promise<void> {
  const bridge = getBridge();
  // De-dupe by media_id (a creative can appear in more than one group).
  const byId = new Map<string, Cacheable>();
  for (const item of groups.flat()) byId.set(item.media_id, item);
  const items = [...byId.values()];
  await Promise.all(
    items.map(async (item) => {
      try {
        await bridge.downloadMedia(item.media_id, item.media_url, item.checksum ?? undefined);
      } catch (err) {
        console.warn(`[mediaCache] prefetch failed for ${item.media_id}`, err);
      }
    }),
  );
  // Evict anything not in the current schedule.
  try {
    await bridge.clearCache(items.map((p) => p.media_id));
  } catch (err) {
    console.warn("[mediaCache] eviction failed", err);
  }
}

/** A playable URL for an item: cached if present, else the live URL. */
export async function resolvePlayableUrl(item: Cacheable): Promise<string> {
  const cached = await getBridge().getCachedMediaUrl(item.media_id);
  return cached ?? item.media_url;
}
